<template>
    <div class="">
        <div class="fixed bg-gray-500 opacity-50 h-screen w-screen top-0 left-0 right-0 z-10"></div>
        <div class="fixed top-0 left-0 right-0 w-full h-full flex items-center justify-center z-20">
            <div @click.stop class="relative flex flex-col justify-between bg-white z-50 max-h-[95vh] w-[95vw] rounded-lg py-10 px-16 overflow-y-scroll no-scrollbar">
                <svg @click="closeWindow" class="w-8 h-8 absolute top-[10px] right-[12px] text-gray-500 cursor-pointer" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor"><path d="m336-280 144-144 144 144 56-56-144-144 144-144-56-56-144 144-144-144-56 56 144 144-144 144 56 56ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/></svg>
                <div ref="printingBlock" class="mb-16 text-sm">
                    <p class="text-xl mb-8">{{ `Отчет по сотруднику: ${salaryAndScheduleDataToPrint.staff.name}` }}</p>
                    <div class="mb-8">
                        <p class="mb-4 text-base">Таблица раписания</p>
                        <div class="flex items-center gap-4 mb-4">
                            <span class="w-full whitespace-nowrap p-1 rounded-lg border border-[#838B9B] text-[#FF8C00] text-center">Дневные часы</span>
                            <span class="w-full whitespace-nowrap p-1 rounded-lg border border-[#838B9B] text-[#0074D9] text-center">Ночные часы</span>
                            <span class="w-full whitespace-nowrap p-1 rounded-lg border border-[#838B9B] bg-[#F5F8CC] text-center">Дневная смена</span>
                            <span class="w-full whitespace-nowrap p-1 rounded-lg border border-[#838B9B] bg-[#DDEBF7] text-center">Ночная смена</span>
                            <span class="w-full whitespace-nowrap p-1 rounded-lg border border-[#838B9B] bg-[#F4CCCC] text-center">Пропуск</span>
                            <span class="w-full whitespace-nowrap p-1 rounded-lg border border-[#838B9B] bg-[#D9EAD3] text-center">Больничный</span>
                            <span class="w-full whitespace-nowrap p-1 rounded-lg border border-[#838B9B] bg-[#FCE5CD] text-center">Отпуск</span>
                            <span class="w-full whitespace-nowrap p-1 rounded-lg border border-[#838B9B] text-center">Пусто</span>
                        </div>
                        <table class="max-w-full w-full border-separate print:border-collapse rounded-lg break-inside-avoid table-auto text-sm">
                            <thead>
                                <tr>
                                    <th class="p-1 text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] rounded-tl-lg"></th>
                                    <th v-for="day in Array.from({ length: 31 }, (_, i) => i + 1)" :key="day" class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] min-w-[2rem] last:rounded-tr-lg">{{ day }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(schedule, scheduleIndex) in schedules" :key="schedule.id">
                                    <td class="p-1 text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af]" :class="{'first:rounded-bl-lg': scheduleIndex === schedules.length - 1}">{{ $globalMethods.getMonthAndYear(schedule.date) }}</td>
                                    <td v-for="element in Object.values(schedule.content)" :key="element" 
                                        class="box-border px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] min-w-[2rem]" 
                                        :class="{
                                            'last:rounded-br-lg': scheduleIndex === schedules.length - 1,  
                                            'bg-[#F5F8CC]': element == 'day shift',
                                            'bg-[#DDEBF7]': element == 'night shift',
                                            'bg-[#F4CCCC]': element == 'absence',
                                            'bg-[#D9EAD3]': element == 'sick leave',
                                            'bg-[#FCE5CD]': element == 'vacation',
                                            'text-[#FF8C00]': element.includes('day hours'),
                                            'text-[#0074D9]': element.includes('night hours'),
                                        }"
                                    >{{ getVerboseScheduleLabel(element) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="mb-8">
                        <p class="text-base mb-4">Таблица расчета заработной платы</p>
                        <table class="max-w-full w-full border-separate print:border-collapse rounded-lg break-inside-avoid table-auto text-sm">
                            <thead>
                                <tr>
                                    <th class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] rounded-tl-lg">Сотрудник</th>
                                    <th class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] min-w-[5.63625rem]">% с продаж</th>
                                    <th class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] min-w-[5.63625rem]">Дневные смены</th>
                                    <th class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] min-w-[5.63625rem]">Ночные смены</th>
                                    <th class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] min-w-[5.63625rem]">Часы за день</th>
                                    <th class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] min-w-[5.63625rem]">Часы за ночь</th>
                                    <th class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] min-w-[5.63625rem]">Итог</th>
                                    <th class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] min-w-[5.63625rem]">Штрафы</th>
                                    <th class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] rounded-tr-lg min-w-[5.63625rem]">Выплачено</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(salaryReport, salaryReportIndex) in salaryReports" :key="salaryReport.staff.id">
                                    <td :class="{'first:rounded-bl-lg': salaryReportIndex == salaryReports.length - 1}" class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af]">{{ $globalMethods.getMonthAndYear(salaryReport.date) }}</td>
                                    <td class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af]">{{ $globalMethods.formatNumberWithSpaces(salaryReport.total_per_sales) || "0.00" }}</td>
                                    <td class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af]">{{ $globalMethods.formatNumberWithSpaces(salaryReport.total_per_day_shifts) || "0.00" }}</td>
                                    <td class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af]">{{ $globalMethods.formatNumberWithSpaces(salaryReport.total_per_night_shifts) || "0.00" }}</td>
                                    <td class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af]">{{ $globalMethods.formatNumberWithSpaces(salaryReport.total_per_day_hours) || "0.00" }}</td>
                                    <td class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af]">{{ $globalMethods.formatNumberWithSpaces(salaryReport.total_per_night_hours) || "0.00" }}</td>
                                    <td class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af]">{{ $globalMethods.formatNumberWithSpaces(salaryReport.accrual_result) || "0.00" }}</td>
                                    <td class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af]">{{ $globalMethods.formatNumberWithSpaces(salaryReport.penalty_payout) || "0.00" }}</td>
                                    <td :class="{'last:rounded-br-lg': salaryReportIndex == salaryReports.length - 1}" class="px-[0.5rem] py-[0.5rem] text-center font-normal outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af]">{{ $globalMethods.formatNumberWithSpaces(salaryReport.payout_result) || "0.00" }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="text-base mb-16">
                        <p class="mb-4">Таблица выплат</p>
                        <table class="max-w-full w-full break-inside-avoid table-auto rounded-lg outline print:outline-none print:border print:border-[#838B9B] outline-[#a5a6af] text-sm">
                            <thead>
                                <tr class="border-b border-[#838B9B]">
                                    <th class="px-[2rem] py-[0.75rem] text-start font-normal">Дата</th>
                                    <th class="px-[2rem] py-[0.75rem] text-start font-normal">Период</th>
                                    <th class="px-[2rem] py-[0.75rem] text-start font-normal">Тип выплаты</th>
                                    <th class="px-[2rem] py-[0.75rem] text-start font-normal">Размер выплаты</th>
                                    <th class="px-[2rem] py-[0.75rem] text-start font-normal">Комментарий</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="payouts.length == 0">
                                    <td colspan="6" class="text-center pt-4">За выбранный период нет выплат</td>
                                </tr>
                                <tr v-for="payout in payouts" :key="payout.id" class="border-b last:border-none border-[#838B9B]" :class="payout.is_deleted ? 'bg-[#f2d0db] hover:bg-red-50' : 'bg-white hover:bg-gray-50'">
                                    <td class="px-[2rem] py-[0.5rem] text-start">{{ $globalMethods.formatDate('ru', payout.datetime_of_created) }}</td>
                                    <td class="px-[2rem] py-[0.5rem] text-start">{{ $globalMethods.getMonthAndYear(payout.target_period) }}</td>
                                    <td class="px-[2rem] py-[0.5rem] text-start">{{ labels.payoutTypes[payout.type] }}</td>
                                    <td class="px-[2rem] py-[0.5rem] text-start">{{ $globalMethods.formatNumberWithSpaces(payout.total) }}</td>
                                    <td class="px-[2rem] py-[0.5rem] text-start">{{ payout.note || " " }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-if="addSignForm" class="flex items-start justify-around mb-16">
                        <div class="flex flex-col items-stretch gap-6">
                            <p class="text-base mb-2">Сотрудник</p>
                            <div class="flex items-center gap-4">
                                <p>ФИО:</p>
                                <p class="ml-4">{{ salaryAndScheduleDataToPrint.staff.name }}</p>
                            </div>
                            <div class="flex items-center gap-4">
                                <p>Телефон:</p>
                                <p class="ml-4">{{ $globalMethods.formatPhoneNumber(salaryAndScheduleDataToPrint.staff.phone) }}</p>
                            </div>
                            <div class="flex items-center gap-4">
                                <p>Подпись:</p>
                                <p class="ml-4">________________________________________</p>
                            </div>
                        </div>
                        <div class="flex flex-col items-stretch gap-6">
                            <p class="text-base mb-2">Учредитель</p>
                            <div class="flex items-center gap-4">
                                <p>ФИО:</p>
                                <p class="ml-4">{{ companyData.founder.name }}</p>
                            </div>
                            <div class="flex items-center gap-4">
                                <p>Телефон:</p>
                                <p class="ml-4">{{ $globalMethods.formatPhoneNumber(companyData.founder.phone) }}</p>
                            </div>
                            <div class="flex items-center gap-4">
                                <p>Подпись:</p>
                                <p class="ml-4">________________________________________</p>
                            </div>
                        </div>
                    </div>
                    <p class="w-full text-right">{{`Дата создания отчета: ${$globalMethods.formatDate('ru', $globalMethods.getCurrentDate())}`}}</p>
                </div>
                <button @click.stop="printData" :disabled="false" type="button" class="text-center bg-custom-blue hover:bg-custom-blue-hover flex items-center justify-center text-white font-medium rounded-lg text-sm px-5 py-2">Напечатать</button>
            </div>
        </div>
    </div>
</template>


<script>
    import { mapState } from 'vuex';

    export default {
        name: "ScheduleAndSalaryDetailWindow",
        components: {},
        props: {
            isOpened: {
                type: Boolean,
                required: true,
            },
            currentPayout: {
                type: Object,
                required: true,
            },
            addSignForm: {
                type: Boolean,
                required: true,
            },
        },
        data() {
            return {
                schedules: [],
                salaryReports: [],
                payouts: [],
            }
        },
        computed: {
            ...mapState(["salaryAndScheduleDataToPrint", "labels", "companyData"]),
        },
        methods: {
            printData() {
                const printContent = this.$refs.printingBlock.innerHTML;
                const newWindow = window.open("", "_blank");

                const styles = [...document.styleSheets].map(sheet => {
                    try {
                        return [...sheet.cssRules].map(rule => rule.cssText).join("\n");
                    } catch (e) {
                        return "";
                    }
                }).join("\n");

                newWindow.document.write(`
                    <html>
                    <head>
                        <title>Печать</title>
                        <style>${styles}</style>
                    </head>
                    <body class="p-8" style="zoom: 0.6;">
                        ${printContent}
                    </body>
                    </html>
                `);

                newWindow.document.close();
                newWindow.print();
                newWindow.close();
            },
            getVerboseScheduleLabel(value) {
                if (!value) return;
                if (this.labels.schedule[value]) return this.labels.schedule[value];
                if (value.includes("day hours")) return value.replace("day hours", "").trim();
                if (value.includes("night hours")) return value.replace("night hours", "").trim();
            },
            getFirstDays(startDateStr, endDateStr) {
                const dates = [];
                let currentDate = new Date(startDateStr);

                while (currentDate <= new Date(endDateStr)) {
                    dates.push(currentDate.toISOString().split("T")[0]);
                    currentDate.setMonth(currentDate.getMonth() + 1);
                }

                return dates;
            },
            closeWindow() {
                document.body.classList.remove("overflow-hidden");
                this.$emit("update:isOpened", false);
            },
            initData() {
                this.schedules = [];
                this.salaryReports = [];
                this.payouts = [...this.salaryAndScheduleDataToPrint.payouts].filter(payout => !payout.is_deleted);
                this.payouts.sort((payout1, payout2) => new Date(payout2.date) - new Date(payout1.date))

                for (const date of this.getFirstDays(this.salaryAndScheduleDataToPrint.date_from, this.salaryAndScheduleDataToPrint.date_to)) {
                    let schedule = this.salaryAndScheduleDataToPrint.schedules.find(schedule => schedule.date == date);
                    if (!schedule) {
                        schedule = {
                            date: date,
                            content: Array.from({ length: 31 }, (_, i) => i + 1).reduce((obj, day) => {
                                obj[day] = ""; 
                                return obj;
                            }, {})
                        }
                    } else {
                        schedule.content[29] = schedule.content[29] || "";
                        schedule.content[30] = schedule.content[30] || "";
                        schedule.content[31] = schedule.content[31] || "";
                    }
                    this.schedules.push(schedule);

                    let salaryReport = this.salaryAndScheduleDataToPrint.salary_reports.find(salary_report => salary_report.date == date);
                    if (!salaryReport) {
                        salaryReport = {
                            "date": date,
                            "staff": this.salaryAndScheduleDataToPrint.staff,
                            "salary_per_day_hour": this.salaryAndScheduleDataToPrint.staff.salary_per_day_hour,
                            "salary_per_day_shift": this.salaryAndScheduleDataToPrint.staff.salary_per_day_shift,
                            "salary_per_night_hour": this.salaryAndScheduleDataToPrint.staff.salary_per_night_hour,
                            "salary_per_night_shift": this.salaryAndScheduleDataToPrint.staff.salary_per_night_shift,
                            "percent_of_sale": this.salaryAndScheduleDataToPrint.staff.percent_of_sale,
                        }
                    }
                    this.salaryReports.push(salaryReport);
                }
            },
        },
        created() {
            this.initData();
        }
    }
</script>